import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Popover, PopoverContent, PopoverTrigger } from "~/atoms/popover";
import { Button } from "~/atoms/button";
import { ListTodo } from "lucide-react";
import { useMyTodos, type TodoItem } from "~/api/v2/useMyTodos";
import { CenterSpinner } from "~/atoms/spinner";
import { useState } from "react";
import { TodoEmpty } from "~/molecules/TodoEmpty";
import {
  getTodoTypeText,
  getTodoIcon,
  getIconColor,
  getIconBackgroundColor,
  getTodoDateString,
  compareTodoOrder,
  getTodoNavigationPath,
} from "~/helpers/todo";
import { useUserContext } from "~/hooks/useUserContext";

interface TodoItemCardProps {
  todo: TodoItem;
  currentUserId?: number;
  onClick: (todo: TodoItem) => void;
}

const TodoItemCard = ({ todo, currentUserId, onClick }: TodoItemCardProps) => {
  const todoTypeText = getTodoTypeText(todo, currentUserId);
  const relativeDate = getTodoDateString(todo);

  const getIconElement = () => {
    const IconComponent = getTodoIcon(todo);
    if (!IconComponent) return null;
    const bgColor = getIconBackgroundColor(todo);
    const iconColor = getIconColor(todo);
    return (
      <div className={`rounded-lg p-1.5 ${bgColor}`}>
        <IconComponent className={`size-4 ${iconColor}`} />
      </div>
    );
  };

  return (
    <div
      className="hover:bg-muted/50 mx-2 my-1 rounded-lg p-3 transition-colors hover:cursor-pointer"
      onClick={() => onClick(todo)}
    >
      <div className="flex items-start gap-3">
        {getIconElement()}
        <div className="min-w-0 flex-1">
          <p className="text-muted-foreground mb-1 text-xs">{todoTypeText}</p>
          <p className="mb-1 line-clamp-2 text-sm font-medium">{todo.title}</p>
          {relativeDate && (
            <p className="text-muted-foreground text-xs">{relativeDate}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export const TodoPopover = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const [open, setOpen] = useState(false);
  const { data, isLoading, mutate } = useMyTodos();

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (open) void mutate();
  };

  const handleTodoClick = (todo: TodoItem) => {
    void navigate(getTodoNavigationPath(todo));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative">
          <ListTodo />
          {data && data.length > 0 && (
            <div className="absolute -top-2 -right-2 rounded-full border bg-white px-1.5 text-xs">
              {data.length}
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 rounded-lg p-0"
        align="end"
        sideOffset={8}
      >
        <div className="p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{t("dashboard.myTodos.title")}</h3>
            {data !== undefined && (
              <span className="text-muted-foreground text-sm">
                {t("dashboard.myTodos.itemsCount", { count: data.length })}
              </span>
            )}
          </div>
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          {isLoading || !data ? (
            <div className="p-8">
              <CenterSpinner />
            </div>
          ) : data.length === 0 ? (
            <TodoEmpty className="h-fit border-0" />
          ) : (
            <>
              <div>
                {data.toSorted(compareTodoOrder).map((todo) => (
                  <TodoItemCard
                    key={todo.id}
                    todo={todo}
                    currentUserId={user.id}
                    onClick={handleTodoClick}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
